import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { HouseholdEvent } from '@shared/events'
import { dayKeyOf, deviceTimezone, startOfWeek, type DayKey, type WeekKey } from '@shared/time'
import { db } from './db'
import { subscribeToSync, type SyncStatus } from './sync'

/** Todos los eventos del espejo local. La interfaz nunca espera a la red. */
export function useEvents(): HouseholdEvent[] {
  return useLiveQuery(() => db.events.toArray(), [], []) as HouseholdEvent[]
}

/**
 * El día de hoy, que se renueva solo al pasar la medianoche.
 *
 * Sin esto, un móvil que se queda abierto en la encimera sigue enseñando la
 * cena de ayer por la mañana.
 */
export function useToday(): DayKey {
  const tz = deviceTimezone()
  const [today, setToday] = useState(() => dayKeyOf(Date.now(), tz))

  useEffect(() => {
    const timer = window.setInterval(() => {
      const current = dayKeyOf(Date.now(), tz)
      setToday((previous) => (previous === current ? previous : current))
    }, 60_000)
    return () => window.clearInterval(timer)
  }, [tz])

  return today
}

export function useCurrentWeek(): WeekKey {
  const today = useToday()
  return useMemo(() => startOfWeek(today), [today])
}

export function useSyncStatus(): { status: SyncStatus; pending: number } {
  const [state, setState] = useState<{ status: SyncStatus; pending: number }>({
    status: 'idle',
    pending: 0,
  })
  useEffect(
    () => subscribeToSync((next) => setState({ status: next.status, pending: next.pending })),
    [],
  )
  return state
}
